import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { has } from "ramda";
import clsx from "clsx";
import { formatBytes, useTimeRemaining } from "../upload/uploadUtils";
import { notBlank } from "../common/general";
import { useTranslation } from "react-i18next";

import Grid from "@material-ui/core/Grid";
import ClearIcon from "@material-ui/icons/Clear";
import LinearProgress from "@material-ui/core/LinearProgress";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";

const useStyles = makeStyles(theme => ({
  bytes: {
    color: "rgba(0, 0, 0, 0.54)",

    direction: "ltr",
  },

  bytesLtr: {
    paddingLeft: 10,
  },

  bytesRtl: {
    paddingRight: 10,
  },

  clearIcon: {
    cursor: "pointer",
  },

  fileName: {
    flex: 1,
    fontSize: 16,
    color: theme.palette.primary.main,
  },

  fileNameRow: {
    marginTop: 20,
  },

  border: {
    borderBottomWidth: 2,
    borderBottomStyle: "solid",
    borderBottomColor: theme.palette.primary.main,
  },

  error: {
    color: "red",
  },

  progress: {
    marginLeft: "auto",
  },

  progressRtl: {
    marginRight: "auto",
  },
}));

const FileRow = ({ file, fileRemoved }) => {
  const classes = useStyles();
  const { i18n } = useTranslation();
  const fileNameRef = React.useRef(null);
  const timeRemaining = useTimeRemaining(file.progress.timeRemaining);

  return (
    <Grid
      item
      container
      direction="row"
      xs
      className={clsx(classes.fileNameRow, {
        [classes.border]: !has("progress", file),
        [classes.error]: has("status", file) && file.status === "failed",
      })}
    >
      <Grid item container justify="space-between" alignItems="center" xs={11}>
        <Grid
          item
          container
          xs={12}
          justify="space-between"
          alignItems="center"
        >
          <Grid ref={fileNameRef} item style={{ maxWidth: 200 }}>
            {fileNameRef.current && fileNameRef.current.offsetWidth < 200 ? (
              <Typography noWrap>{file.name}</Typography>
            ) : (
              <Tooltip title={file.name}>
                <Typography noWrap>{file.name}</Typography>
              </Tooltip>
            )}
          </Grid>

          {notBlank(file.size) && (
            <Grid
              item
              className={clsx(classes.bytes, {
                [classes.bytesLtr]: i18n.dir() === "ltr",
                [classes.bytesRtl]: i18n.dir() === "rtl",
              })}
            >
              <span>{formatBytes(file.size)}</span>
            </Grid>
          )}

          {has("progress", file) && (
            <Grid
              item
              className={clsx({
                [classes.progress]: i18n.dir() === "ltr",
                [classes.progressRtl]: i18n.dir() === "rtl",
              })}
            >
              <Typography style={{ direction: "ltr" }}>
                {notBlank(file.progress.timeRemaining) &&
                  !isNaN(file.progress.timeRemaining) &&
                  file.progress.timeRemaining > 0 &&
                  `${timeRemaining} • `}
                {notBlank(file.progress.percent) && i18n.dir() === "rtl"
                  ? `%${file.progress.percent}`
                  : `${file.progress.percent}%`}
              </Typography>
            </Grid>
          )}
        </Grid>

        <Grid item xs={12}>
          {has("progress", file) && (
            <LinearProgress
              variant="determinate"
              value={file.progress.percent}
            />
          )}
        </Grid>
      </Grid>

      <Grid item container alignItems="center" xs={1}>
        <ClearIcon
          className={classes.clearIcon}
          onClick={() => fileRemoved(file)}
        />
      </Grid>
      {has("status", file) && file.status === "failed" && (
        <div className={classes.error}>
          {has("error", file) && notBlank(file.error)
            ? file.error
            : "Unable to upload this file, please remove and try again."}
        </div>
      )}
    </Grid>
  );
};

export default FileRow;
